import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react"
import { createPortal } from "react-dom"
import checkboxAndroidOff from "../assets/figma-garage-lifecycle/checkbox-android-off.svg"
import checkboxAndroidOn from "../assets/figma-garage-lifecycle/checkbox-android-on.svg"
import checkboxIosOff from "../assets/figma-garage-lifecycle/checkbox-ios-off.svg"
import checkboxIosOn from "../assets/figma-garage-lifecycle/checkbox-ios-on.svg"
import starsAndroid from "../assets/figma-garage-lifecycle/stars-android.svg"
import starsIos from "../assets/figma-garage-lifecycle/stars-ios.svg"
import successAndroid from "../assets/figma-garage-lifecycle/success-android.svg"
import successIos from "../assets/figma-garage-lifecycle/success-ios.svg"
import deactivatedAndroid from "../assets/figma-garage-lifecycle/deactivated-android.svg"
import deactivatedIos from "../assets/figma-garage-lifecycle/deactivated-ios.svg"
import cleanedAndroid from "../assets/figma-garage-lifecycle/cleaned-android.svg"
import cleanedIos from "../assets/figma-garage-lifecycle/cleaned-ios.svg"
import { formatPlateForDisplay, isValidRussianPlate } from "../utils/plate"
import { useI18n } from "../hooks/useI18n"
import "./GarageLifecycleSheets.css"

function Checkbox({ checked, isIOS }: { checked: boolean; isIOS: boolean }) {
  const src = isIOS
    ? checked ? checkboxIosOn : checkboxIosOff
    : checked ? checkboxAndroidOn : checkboxAndroidOff
  return <img className="bbf-garage-sheet-checkbox-icon" src={src} alt="" />
}

function useGrabberDrag(onClose: () => void) {
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finish = (event: ReactPointerEvent<HTMLElement>, allowClose: boolean) => {
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) onClose()
  }

  return { dragOffset, isDragging, onPointerDown, onPointerMove, finish }
}

export function GarageSuccessNotification({ isIOS, text, kind }: { isIOS: boolean; text: string; kind: "activation" | "clean" | "deactivate" }) {
  const { direction } = useI18n()
  const src = kind === "clean"
    ? isIOS ? cleanedIos : cleanedAndroid
    : kind === "deactivate"
      ? isIOS ? deactivatedIos : deactivatedAndroid
      : isIOS ? successIos : successAndroid
  const notice = (
    <div className={`bbf-garage-success ${kind}`} role="status">
      <img src={src} alt="" />
      <span dir={direction}>{text}</span>
    </div>
  )
  const host = typeof document !== "undefined" ? document.querySelector(".bbf-garage-screen") : null
  return host ? createPortal(notice, host) : notice
}

export function GarageActivationSheet({
  isIOS,
  mode = "activate",
  plate,
  promoCode,
  checked,
  busy,
  plateError,
  promoValid = false,
  promoError = "",
  bonusYears = 0,
  promoAmountStars = null,
  promoDurationLabel = null,
  onPlateChange,
  onPromoChange,
  onToggle,
  onBack,
  onConfirm,
}: {
  isIOS: boolean
  mode?: "activate" | "attach"
  plate: string
  promoCode: string
  checked: boolean
  busy: boolean
  plateError?: string
  promoValid?: boolean
  promoError?: string
  bonusYears?: number
  promoAmountStars?: number | null
  promoDurationLabel?: string | null
  onPlateChange?: (value: string) => void
  onPromoChange: (value: string) => void
  onToggle: () => void
  onBack: () => void
  onConfirm: () => void
}) {
  const { locale, direction, t } = useI18n()
  const attachMode = mode === "attach"
  const plateDisplay = formatPlateForDisplay(plate)
  const plateReady = isValidRussianPlate(plate)
  const promoReady = attachMode || !promoCode || promoValid
  const confirmDisabled = !checked || !plateReady || Boolean(plateError) || !promoReady || busy
  const durationYears = promoValid ? 1 + bonusYears : 1
  const activationDuration = promoValid && promoDurationLabel
    ? promoDurationLabel
    : new Intl.NumberFormat(locale, { style: "unit", unit: "year", unitDisplay: "long" }).format(durationYears)
  const activationPrice = promoValid && promoAmountStars !== null ? promoAmountStars : 990
  const [keyboardMode, setKeyboardMode] = useState(false)

  useEffect(() => {
    const syncKeyboardMode = (event: Event) => {
      setKeyboardMode((event as CustomEvent<boolean>).detail === true)
    }

    setKeyboardMode(document.documentElement.dataset.bbGarageKeyboard === "true")
    window.addEventListener("bb:garage-keyboard", syncKeyboardMode as EventListener)
    return () => window.removeEventListener("bb:garage-keyboard", syncKeyboardMode as EventListener)
  }, [])

  return (
    <div className={`bbf-garage-sheet-overlay activation-overlay ${isIOS ? "platform-ios" : "platform-android"}`} onClick={onBack}>
      <section className={`bbf-garage-sheet activation ${attachMode ? "attach-mode" : ""} ${keyboardMode ? "keyboard-mode" : ""}`} onClick={(event) => event.stopPropagation()} aria-label={t("garageSheet.activationTitle")}>
        <div className="bbf-garage-sheet-scroll">
        <i className="bbf-garage-sheet-grabber" />
        <h2 dir={direction}>{t("garageSheet.activationTitle")}</h2>
        <input
          className={`bbf-garage-sheet-field plate-field ${plate ? "has-value" : ""} ${plateError ? "invalid" : ""}`}
          value={plateDisplay}
          onChange={(event) => onPlateChange?.(event.target.value)}
          readOnly={!onPlateChange}
          aria-invalid={Boolean(plateError)}
          placeholder="А 000 АА 000"
        />
        <p className="bbf-garage-sheet-helper plate" dir={direction}>{t("garageSheet.plateHelper")}</p>
        {plateError && <p className="bbf-garage-sheet-field-error plate" dir={direction}>{plateError}</p>}
        {!attachMode && (
          <>
            <input
              className={`bbf-garage-sheet-field promo ${promoCode ? "has-value" : ""} ${promoError ? "invalid" : ""}`}
              value={promoCode}
              onChange={(event) => onPromoChange(event.target.value)}
              maxLength={15}
              aria-invalid={Boolean(promoError)}
              placeholder="PROMOCODE"
            />
            <p className="bbf-garage-sheet-helper promo" dir={direction}>{t("garageSheet.promoHelper")}</p>
            {promoError && <p className="bbf-garage-sheet-field-error promo" dir={direction}>{promoError}</p>}
            <p className="bbf-garage-sheet-bonus" dir={direction}>{t("garageSheet.cleanBonusLine1")}<br />{t("garageSheet.cleanBonusLine2")}</p>
            <p className="bbf-garage-sheet-duration" dir={direction}>{t("garageSheet.activationFor")} {activationDuration}</p>
            <div className="bbf-garage-sheet-price" dir={direction}>{t("garageSheet.cost")} {activationPrice} <img src={isIOS ? starsIos : starsAndroid} alt="Stars" /></div>
          </>
        )}
        <button className="bbf-garage-sheet-consent" type="button" aria-pressed={checked} onClick={onToggle}>
          <Checkbox checked={checked} isIOS={isIOS} />
          <span dir={direction}>{t("garageSheet.ownerConsentLine1")}<br />{t("garageSheet.ownerConsentLine2")}<br />{t("garageSheet.ownerConsentLine3")}</span>
        </button>
        <button className="bbf-garage-sheet-button back" onClick={onBack} dir={direction}>{t("common.back")}</button>
        <button className="bbf-garage-sheet-button primary" disabled={confirmDisabled} onClick={onConfirm} dir={direction}>
          {busy ? t("garageSheet.processing") : attachMode ? t("garageSheet.attach") : t("garageSheet.pay")}
        </button>
        </div>
      </section>
    </div>
  )
}

type LifecycleKind = "clean" | "deactivate"

export function GarageLifecycleSheet({
  kind,
  isIOS,
  checked,
  busy,
  free = false,
  onToggle,
  onBack,
  onConfirm,
}: {
  kind: LifecycleKind
  isIOS: boolean
  checked: boolean
  busy: boolean
  free?: boolean
  onToggle: () => void
  onBack: () => void
  onConfirm: () => void
}) {
  const { direction, t } = useI18n()
  const clean = kind === "clean"
  const title = clean ? t("garageLifecycle.cleanTitle") : t("garageLifecycle.deactivateTitle")
  const drag = useGrabberDrag(onBack)
  return (
    <div className={`bbf-garage-sheet-overlay ${kind}-overlay ${isIOS ? "platform-ios" : "platform-android"}`} onClick={onBack}>
      <section
        className={`bbf-garage-sheet ${kind}${clean && drag.isDragging ? " is-dragging" : ""}`}
        style={clean ? ({ "--bbf-garage-sheet-drag-y": `${drag.dragOffset}px` } as CSSProperties) : undefined}
        onClick={(event) => event.stopPropagation()}
        aria-label={title}
      >
        <div className="bbf-garage-sheet-scroll">
        <i
          className="bbf-garage-sheet-grabber"
          onPointerDown={clean ? drag.onPointerDown : undefined}
          onPointerMove={clean ? drag.onPointerMove : undefined}
          onPointerUp={clean ? (event) => drag.finish(event, true) : undefined}
          onPointerCancel={clean ? (event) => drag.finish(event, false) : undefined}
        />
        <h2 dir={direction}>{title}</h2>
        <p className="bbf-garage-sheet-warning" dir={direction}>
          {clean ? <>{t("garageLifecycle.cleanWarningLine1")}<br />{t("garageLifecycle.cleanWarningLine2")}</> : <>{t("garageLifecycle.deactivateWarningLine1")}<br />{t("garageLifecycle.deactivateWarningLine2")}</>}
        </p>
        {clean ? (
          <>
            <h3 dir={direction}>{t("garageLifecycle.willBeDeleted")}</h3>
            <p className="bbf-garage-sheet-list removed" dir={direction}>{t("garageLifecycle.cleanRemovedLine1")}<br />{t("garageLifecycle.cleanRemovedLine2")}<br />{t("garageLifecycle.cleanRemovedLine3")}<br />{t("garageLifecycle.cleanRemovedLine4")}</p>
            <h3 className="preserved-title" dir={direction}>{t("garageLifecycle.willRemain")}</h3>
            <p className="bbf-garage-sheet-list preserved" dir={direction}>{t("garageLifecycle.cleanPreservedLine1")}<br />{t("garageLifecycle.cleanPreservedLine2")}<br />{t("garageLifecycle.cleanPreservedLine3")}<br />{t("garageLifecycle.cleanPreservedLine4")}</p>
            <div className={`bbf-garage-sheet-price ${free ? "free" : ""}`} dir={direction}>
              {free ? t("garageLifecycle.freeFirstHour") : <>{t("garageSheet.cost")} 9 900 <img src={isIOS ? starsIos : starsAndroid} alt="Stars" /></>}
            </div>
          </>
        ) : (
          <>
            <h3 dir={direction}>{t("garageLifecycle.willBecomeUnavailable")}</h3>
            <p className="bbf-garage-sheet-list removed" dir={direction}>{t("garageLifecycle.deactivateRemovedLine1")}<br />{t("garageLifecycle.deactivateRemovedLine2")}<br />{t("garageLifecycle.deactivateRemovedLine3")}<br />{t("garageLifecycle.deactivateRemovedLine4")}<br />{t("garageLifecycle.deactivateRemovedLine5")}<br />{t("garageLifecycle.deactivateRemovedLine6")}<br />{t("garageLifecycle.deactivateRemovedLine7")}<br />{t("garageLifecycle.deactivateRemovedLine8")}</p>
            <h3 className="preserved-title" dir={direction}>{t("garageLifecycle.willRemainAvailable")}</h3>
            <p className="bbf-garage-sheet-list preserved" dir={direction}>{t("garageLifecycle.deactivatePreservedLine1")}<br />{t("garageLifecycle.deactivatePreservedLine2")}<br />{t("garageLifecycle.deactivatePreservedLine3")}<br />{t("garageLifecycle.deactivatePreservedLine4")}<br />{t("garageLifecycle.deactivatePreservedLine5")}<br />{t("garageLifecycle.deactivatePreservedLine6")}</p>
          </>
        )}
        <button className="bbf-garage-sheet-consent" type="button" aria-pressed={checked} onClick={onToggle}>
          <Checkbox checked={checked} isIOS={isIOS} />
          <span dir={direction}>{clean ? <>{t("garageLifecycle.cleanConsentLine1")}<br />{t("garageLifecycle.cleanConsentLine2")}<br />{t("garageLifecycle.cleanConsentLine3")}</> : <>{t("garageLifecycle.deactivateConsentLine1")}<br />{t("garageLifecycle.deactivateConsentLine2")}<br />{t("garageLifecycle.deactivateConsentLine3")}</>}</span>
        </button>
        {clean && <button className="bbf-garage-sheet-button back" onClick={onBack} dir={direction}>{t("common.back")}</button>}
        <button
          className={`bbf-garage-sheet-button ${clean ? "primary" : "danger wide"}`}
          disabled={!checked || busy}
          onClick={onConfirm}
          dir={direction}
        >
          {clean ? busy ? t("garageSheet.processing") : free ? t("garageLifecycle.clean") : t("garageSheet.pay") : t("garageLifecycle.deactivate")}
        </button>
        </div>
      </section>
    </div>
  )
}
